<ion-header>
    <ion-toolbar color="secondary">
        <ion-title>Terms of Use</ion-title>

        <ion-buttons slot="end">
            <ion-button (click)="onClose()">
                <i class="material-symbols-outlined">close</i>
            </ion-button>
        </ion-buttons>
    </ion-toolbar>
</ion-header>

<ion-content fullscreen>
    <main>
        <section>
            <h2>Effective Date: 12/2025</h2>
            <p>
                These Terms of Use ("Terms") form a binding agreement between you ("you" or "user") and Drive &amp; Tow LLC ("Drive
                &amp; Tow," "we," "our," or "us"). By creating an account, downloading, or using the Drive &amp; Tow mobile
                application and related services (the "App"), you agree to these Terms. Please read them carefully.
            </p>

            <h2>Eligibility</h2>

            <ul>
                <li>You must be at least 18 years old to use Drive &amp; Tow.</li>
                <li>By using the App, you represent and warrant that you are legally permitted to enter into this agreement.</li>
            </ul>

            <h2>Account Registration</h2>
            <ul>
                <li>To use the App, you must create an account and provide accurate personal information.</li>
                <li>You are responsible for maintaining the confidentiality of your login credentials.</li>
                <li>You agree to notify us immediately of any unauthorized use of your account.</li>
            </ul>

            <h2>Services Provided</h2>
            <p>
                Drive &amp; Tow is a technology platform that connects cargo/trailer owners with independent drivers who have
                suitable pickup trucks.
            </p>
            <ul>
                <li>Drive &amp; Tow acts solely as a mediator to facilitate these connections.</li>
                <li>We do not own or operate vehicles, handle cargo, or provide transportation services ourselves.</li>
                <li>
                    All transportation services are provided by independent drivers, who are not employees, agents, or
                    representatives of Drive &amp; Tow.
                </li>
                <li>
                    Unlike rideshare platforms that transport people, Drive &amp; Tow is limited to assisting with small personal
                    cargo (such as furniture, appliances, or personal-use trailers).
                </li>
                <li>Drive &amp; Tow is not intended for heavy commercial freight, oversized industrial loads, or any cargo that exceeds standard pickup truck safety limits.</li>
                <li>Drive &amp; Tow does not allow the transport of passengers, animals, fireworks, drugs, weapons, fine art, hazardous materials, or illegal items.</li>
                <li>
                    Drivers are not automatically responsible for loading or unloading cargo into their truck beds. Loading and unloading responsibilities must be agreed 
                    between the driver and requester through the App's chat feature, or confirmed together at the pickup location. Drivers are, however, responsible for 
                    properly hitching and securing trailers, as they operate the vehicle.
                </li>
            </ul>

            <h2>Payments</h2>
            <p>
                By providing your phone number and opting in within your account settings, you consent to receive automated SMS text
                messages from Drive &amp; Tow. These messages are limited to job status notifications (such as driver matches, trip
                updates, or account alerts).
            </p>
            <ul>
                <li><strong>Required:</strong> You must actively enable SMS notifications in your account settings.</li>
                <li>
                    <strong>Opt-Out:</strong> You may opt out at any time by updating your preferences in your account settings.
                </li>
                <li><strong>Rates:</strong> Standard message and data rates may apply.</li>
                <li>
                    <strong>Data Handling:</strong> All information collected for text messaging is handled securely and in
                    accordance with our
                    <span class="link" (click)="onOpenPrivacyPolicy()">Privacy Policy.</span>
                </li>
            </ul>

            <h2>User Responsibilities</h2>
            <p>You agree not to:</p>
            <ul>
                <li>Provide false or misleading information.</li>
                <li>Use the App for unlawful purposes.</li>
                <li>Attempt to damage, disable, or interfere with the App.</li>
                <li>Circumvent safety or verification measures.</li>
                <li>Decline or misuse required device permissions (such as location, camera, or notifications) that are necessary for proper App functionality.</li>
                <li>
                    Request the transport of passengers, animals, fireworks, drugs, weapons, fine art, hazardous materials, illegal items, heavy commercial freight, 
                    oversized industrial loads, or any cargo that exceeds standard pickup truck safety limits.
                </li>
            </ul>
            <p>Drivers further agree to:</p>
            <ul>
                <li>Maintain valid insurance and licenses.</li>
                <li>Operate vehicles safely and in compliance with applicable laws.</li>
                <li>Provide their own vehicles, fuel, and equipment necessary to complete transportation jobs.</li>
                <li>
                    Not accept jobs that involve transporting passengers, animals, fireworks, drugs, weapons, fine art, hazardous materials, illegal items, 
                    heavy commercial freight, oversized industrial loads, or any cargo that exceeds standard pickup truck safety limits.
                </li>
            </ul>

            <h2>Vehicle Eligibility Requirements</h2>
            <p>Drivers using the Drive &amp; Tow App must operate pickup trucks that meet the platform’s vehicle eligibility guidelines. As a general eligibility standard, pickup trucks within 10 model years of the current calendar year are eligible to use the platform. Vehicles between 11 and 15 model years may be considered on a case-by-case basis after additional review of vehicle information, condition, and towing capability. <strong>Pickup trucks older than 15 model years are not eligible</strong> for use on the platform due to safety and reliability considerations.</p>
            <p>Drive &amp; Tow reserves the right to deny, limit, or revoke vehicle eligibility at any time based on safety, compliance, or operational concerns.</p>

            <h2>Independent Contractor Status</h2>
            <ul>
                <li>
                    <strong>All drivers who use the Drive &amp; Tow App are independent contractors, not employees, agents, joint venturers,
                    or partners of Drive &amp; Tow.</strong>
                </li>
                <li>
                    Nothing in these Terms shall be construed as creating an employment relationship between Drive &amp; Tow and any
                    driver.
                </li>
                <li>
                    Drivers are <strong>solely responsible</strong> for:
                    <ul>
                        <li>Maintaining required licenses, registrations, and insurance.</li>
                        <li>Complying with applicable laws and safety regulations.</li>
                        <li>Determining their own availability, acceptance of jobs, and working hours.</li>
                    </ul>
                </li>
                <li>
                    Drive &amp; Tow does not control or direct drivers in the operation of their vehicles, the manner in which
                    transportation is performed, or the prices they choose to accept (beyond app-based job cost structure).
                </li>
                <li>
                    Cargo/trailer owners and drivers acknowledge that Drive &amp; Tow's role is strictly as a mediator providing a
                    technology platform to connect users, and that all transportation agreements are made directly between the
                    driver and the cargo/trailer owner.
                </li>
            </ul>

            <h2>Safety and Disclaimers</h2>
            <ul>
                <li>
                    Drive &amp; Tow does not guarantee the quality, safety, or legality of services provided by drivers or trailer
                    owners.
                </li>
                <li>Drive &amp; Tow provides a technology platform that outlines general safety guidelines and limits based on vehicle age, towing and payload capacity, and the type of items requested for transport. Drivers are solely responsible for ensuring their vehicles meet all applicable requirements and are operated safely and in compliance with all laws.</li>
                <li>We are not responsible for loss, damage, or disputes between users.</li>
                <li>Use of the App is at your own risk.</li>
            </ul>

            <h2>License to Use the App</h2>
            <p>
                Drive &amp; Tow grants you a limited, non-exclusive, non-transferable, revocable license to use the App solely for
                its intended purpose. We retain all rights, title, and interest in the App.
            </p>

            <h2>Limitation of Liability</h2>
            <p>To the maximum extent permitted by law:</p>
            <ul>
                <li>
                    <strong>Drive &amp; Tow provides the App as a technology platform only.</strong> We are not responsible for the actions, conduct,
                    or services of drivers, cargo/trailer owners, or any other users of the App.
                </li>
                <li>
                    We do not guarantee the safety, quality, or legality of any transport service arranged through the App. All
                    agreements for transport are strictly between users.
                </li>
                <li>
                    Drive &amp; Tow is not liable for loss, theft, damage, delay, injury, or disputes arising from use of the App.
                </li>
                <li>
                    If a claim is made, Drive &amp; Tow's responsibility is limited strictly to the cost of the job fee paid through
                    the App for that pickup and delivery.
                </li>
                <li>
                    Drive &amp; Tow does not cover or insure the value of trailers, cargo, or personal property being transported.
                    Users are responsible for their own insurance coverage.
                </li>
                <li>Drive &amp; Tow does not assume custody, possession, or control of any cargo or trailers at any time.</li>
            </ul>

            <h2>Termination</h2>
            <p>We may suspend or terminate your account if you:</p>
            <ul>
                <li>Violate these Terms,</li>
                <li>Provide false information, or</li>
                <li>Engage in harmful, fraudulent, or unlawful activity.</li>
            </ul>
            <p>You may close your account at any time through your account settings.</p>

            <h2>Governing Law &amp; Disputes</h2>
            <ul>
                <li>
                    These Terms are governed by and construed in accordance with the laws of the State of Florida, USA, without
                    regard to conflict of law principles.
                </li>
                <li>
                    Any dispute, claim, or controversy arising out of or relating to these Terms or your use of the App shall be
                    resolved through binding arbitration in the State of Florida, or in a Florida small claims court if applicable,
                    unless prohibited by law.
                </li>
                <li>
                    If you access the App from outside the United States, you are responsible for compliance with local laws, but
                    you agree that Florida law governs these Terms.
                </li>
            </ul>

            <h2>International Use</h2>
            <ul>
                <li>
                    Drive &amp; Tow is operated from the United States and governed by the laws of the State of Florida, USA. We
                    make no representation that the App is appropriate, available, or lawful for use in other jurisdictions.
                </li>
                <li>
                    If you access or use the App outside the United States, you do so at your own risk and are solely responsible
                    for complying with all local laws and regulations.
                </li>
                <li>
                    By using the App, you agree that regardless of your location, these Terms are governed by the laws of the State
                    of Florida, USA, and any disputes will be resolved as described in Section 12 (Governing Law &amp; Disputes).
                </li>
            </ul>

            <h2>Changes to Terms</h2>
            <p>
                We may update these Terms from time to time. Changes will be posted within the App with an updated effective date.
                Continued use of the App after changes constitutes acceptance.
            </p>

            <h2>Contact Us</h2>
            <p>
                If you have questions or concerns about this Terms, please contact us at:
                <a [href]="'mailto:' + email">{{ email }}</a>
            </p>
        </section>
    </main>
</ion-content>

<ion-footer class="ion-no-border">
    <ion-button type="button" expand="block" (click)="onClose()"> Close </ion-button>
</ion-footer>
